import Paper, { PaperProps } from '@mui/material/Paper';
import Draggable from 'react-draggable';
import { useState, useEffect, ChangeEvent } from 'react';
import { getOfferMarketDetails, createOffer, getValueCoefficientAndPercentage, handleUnauthorizedError } from '../Services/OfferService';
import OfferDetails from '../Models/OfferDetails';
import CalculateCodValueInput from '../Models/CalculateCodValueInput'
import CodValueResult from '../Models/CodValueResult'
import OfferPost from '../Models/OfferPost'
import { Dialog, DialogTitle, DialogContent, FormControlLabel, Checkbox, Typography, Box, FormControl, InputLabel, Select, MenuItem, FormHelperText, OutlinedInput, InputAdornment, DialogActions, Button, CircularProgress } from '@mui/material';
import SnackbarMessage from "./SnackbarMessage";
import OfferFormSection from './OfferFormSection';
import React from 'react';
import { SelectChangeEvent } from '@mui/material/Select';
import { MESSAGES } from '../Services/MessagesTexts';
import { FaFish } from 'react-icons/fa';
import { FaShip } from 'react-icons/fa';
import { MdPieChart } from 'react-icons/md';
import { AiOutlineNumber } from 'react-icons/ai';
import debounce from 'lodash.debounce';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Ship from '../Models/Ship';
import { formatNumber, formatNumberFloat } from '../Services/FormatService';

type OfferFormDialogProps = {
    open: boolean;
    onClose: (offerSubmitted: boolean) => void;
    tonQuotaTypes: number[];
};

const DEBOUNCE_TIME = 300;

function PaperComponent(props: PaperProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    return (
        <Draggable
            disabled={isMobile}
            handle="#offer-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} />
        </Draggable>
    );
}

export default function OfferFormDialog({ open, onClose, tonQuotaTypes: tonQuotaTypes }: OfferFormDialogProps) {

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const offerDetailsInit = { id: 0, offerMarketNumber: 0, validFrom: new Date(), validTo: new Date(), minimumOffer: 0, offerQuota: [], paymentQuota: [], ships: [], allocationFee: 0 };
    const [offerDetails, setOfferDetails] = useState<OfferDetails>(offerDetailsInit);
    const offerFormInit: OfferPost = { shipNumber: undefined, offerQuotaType: undefined, offerQuantity: undefined, paymentQuotaType: 1, paymentQuantity: undefined };
    const [formData, setFormData] = useState<OfferPost>(offerFormInit);
    const [percentageOfCodValueOffered, setPercentageOfCodValueOffered] = useState<number | null>(null);
    const [valueCoefficient, setValueCoefficient] = useState<number | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [shipNumberError, setShipNumberError] = useState({ isError: false, message: "", hasNoShip: false });
    const [offerQuotaTypeError, setOfferQuotaTypeError] = useState({ isError: false, message: "" });
    const [offerQuantityError, setOfferQuantityError] = useState({ isError: false, message: "" });
    const [paymentQuotaTypeError, setPaymentQuotaTypeError] = useState({ isError: false, message: "" });
    const [paymentQuantityError, setPaymentQuantityError] = useState({ isError: false, message: "" });
    const [valueCoefficientError, setValueCoefficientError] = useState({ isError: false, message: "" });
    const [percentageOfCodValueOfferedError, setPercentageOfCodValueOfferedError] = useState({ isError: false, message: "" });
    const [isFetchValueCoefficientAndPercentage, setIsFetchValueCoefficientAndPercentage] = useState(false);
    const [rawOfferQuantity, setRawOfferQuantity] = useState<string>("");
    const [rawPaymentQuantity, setRawPaymentQuantity] = useState<string>("");
    const [isChecked, setIsChecked] = useState(false);

    const [snackbarData, setSnackbarData] = React.useState<{
        message: string;
        type: 'success' | 'error' | 'info' | 'warning';
    } | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const isBusy = isSubmitting || isLoading;
    const [snackbarOpen, setSnackbarOpen] = React.useState(false);
    const averagePrice = offerDetails.paymentQuota.find(item => item.quotaType === formData.paymentQuotaType)?.averagePrice || 'N/A';
    const formattedAveragePrice = averagePrice !== 'N/A' ? formatNumberFloat(averagePrice.toString()) : 'N/A';

    /**
     * Determines if the ship array is empty, signifying no ships.
     * 
     * @param {Ship[]} ships - Array of ship objects to check.
     * @returns {boolean} True if no ships are present, false otherwise.
     */
    const hasNoShip = (ships: Ship[]): boolean => {
        return ships.length === 0;
    };

    useEffect(() => {
        fetchOfferDetails();
    }, []);

    /**
     * Fetches and processes offer details for the market form.
     * It sets form data for a single ship and handles errors or the absence of ships.
     */
    const fetchOfferDetails = async () => {
        setIsLoading(true);
        try {
            const data: OfferDetails = await getOfferMarketDetails();
            setOfferDetails(data);
            const hasSingleShip = data.ships.length === 1;
            if (hasSingleShip) {
                setFormData(prevState => ({
                    ...prevState,
                    shipNumber: data.ships[0].number,
                    offerMarketNumber: data.offerMarketNumber
                }));
            } else if (hasNoShip(data.ships)) {
                setShipNumberError({ isError: true, message: MESSAGES.SHIP_REGISTRATION_REQUIRED, hasNoShip: true });
            }
            setIsLoading(false);
        } catch (error) {
            handleUnauthorizedError(error);
            setIsLoading(false);
            setSnackbarOpen(true);
            setSnackbarData({ message: MESSAGES.FETCH_ERROR, type: 'error' });
            console.error(error);
        }
    }

    /**
     * Triggers the fetchCodValueRatio() function to calculate the cod value ratio.
     * Invoked when there is a change in formData. 
     */
    useEffect(() => {
        if (isFormDataComplete(formData)) {
            const hasError = handleFormValidation();
            if (!hasError) {
                debouncedFetchValueCoefficientAndPercentage();

            } else {
                setPercentageOfCodValueOffered(null);
                setValueCoefficient(null);
            }
        } else {
            setPercentageOfCodValueOffered(null);
            setValueCoefficient(null);
        }
        return () => {
            debouncedFetchValueCoefficientAndPercentage.cancel();
        };
    }, [formData]);

    const validateShipNumber = (localFormData: OfferPost) => {
        if (hasNoShip(offerDetails.ships)) {
            if (!localFormData.shipNumber) {
                setShipNumberError({ isError: true, message: MESSAGES.SELECT_SHIP_TYPE, hasNoShip: false });
                return true;
            }
        }
        setShipNumberError({ isError: false, message: "", hasNoShip: false });
        return false;
    };

    const validateOfferQuotaType = (localFormData: OfferPost) => {
        if (!localFormData.offerQuotaType) {
            setOfferQuotaTypeError({ isError: true, message: MESSAGES.SELECT_FISH_TYPE_FOR_PURCHASE });
            return true;
        }
        setOfferQuotaTypeError({ isError: false, message: "" });
        return false;
    };

    /**
     * `validateOfferQuantity` is a function that ensures the offer quantity entered in a form meets specific criteria.
     * It takes in `localFormData`, an object containing the form's current state, and a `skipEmptyValueCheck` boolean
     * that determines whether to bypass validation for empty values. This function is used to validate the offerQuantity field
     * within the form, checking it against quota details from `offerDetails.offerQuota`.
     *
     * When `skipEmptyValueCheck` is `true`, the function will not set an error for an empty quantity, which is the case
     * when a quota type is changed and the quantity field is still empty. This avoids presenting an error message to the user
     * prematurely. If `skipEmptyValueCheck` is `false`, and the quantity is empty or exceeds the maximum allowed for the selected
     * quota, it sets an error with an appropriate message sourced from `MESSAGES`.
     *
     * The function returns `true` if an error is set, indicating that the quantity is either empty or 0 when it shouldn't be or exceeds
     * the maximum allowed quantity. Otherwise, it returns `false`, meaning the quantity is valid.
     */
    const validateOfferQuantity = (localFormData: OfferPost, skipEmptyValueCheck: boolean) => {
        // If the quota type is empty, reset the error state and return false.
        if (!localFormData.offerQuotaType) {
            setOfferQuantityError({ isError: false, message: "" });
            return false;
        }
        const foundQuota = offerDetails.offerQuota.find(quota => quota.quotaType === localFormData.offerQuotaType);
        if (localFormData.offerQuantity == 0 && localFormData.offerQuantity.toString() !== "") {
            let message = MESSAGES.QUANTITY_OVER + "."
            if (formData.offerQuotaType) {
                const isTonQuotaType = tonQuotaTypes.some(tqt => tqt === formData.offerQuotaType);
                if (isTonQuotaType) {
                    message = MESSAGES.QUANTITY_OVER + " tonn."
                } else {
                    message = MESSAGES.QUANTITY_OVER + " kg."
                }
            }
            setOfferQuantityError({ isError: true, message: message });
            return true;
        }
        else if (!localFormData.offerQuantity) {
            if (skipEmptyValueCheck) {
                setOfferQuantityError({ isError: false, message: "" });
            } else if (foundQuota) {
                const maxAllowedQuotaQuantity = foundQuota.quantity;
                setOfferQuantityError({
                    isError: true,
                    message: MESSAGES.SELECT_QUANTITY_FOR_PURCHASE +
                        MESSAGES.MAX_QUOTA(
                            maxAllowedQuotaQuantity,
                            formData.offerQuotaType && tonQuotaTypes.some(tqt => tqt === formData.offerQuotaType) ? "tonn" : "kg"
                        )
                });
            } else {
                setOfferQuantityError({ isError: true, message: MESSAGES.SELECT_QUANTITY_FOR_PURCHASE });
            }
            return true;
        } else {
            if (!foundQuota) {
                setOfferQuantityError({ isError: true, message: MESSAGES.QUANTITY_CALCULATION_ERROR });
                return true;
            } else {
                const maxAllowedQuotaQuantity = foundQuota.quantity;
                if (localFormData.offerQuantity > maxAllowedQuotaQuantity) {
                    setOfferQuantityError({
                        isError: true,
                        message: MESSAGES.MAX_QUOTA(
                            maxAllowedQuotaQuantity,
                            formData.offerQuotaType && tonQuotaTypes.some(tqt => tqt === formData.offerQuotaType) ? "tonn" : "kg"
                        )
                    });
                    return true;
                }
            }
        }
        setOfferQuantityError({ isError: false, message: "" });
        return false;
    };

    const validatePaymentQuotaType = (localFormData: OfferPost) => {
        if (!localFormData.paymentQuotaType) {
            setPaymentQuotaTypeError({ isError: true, message: MESSAGES.SELECT_FISH_TYPE_FOR_PAYMENT });
            return true;
        }
        setPaymentQuotaTypeError({ isError: false, message: "" });
        return false;
    };

    const validatePaymentQuantity = (localFormData: OfferPost) => {
        if (localFormData.paymentQuantity == 0 && localFormData.paymentQuantity.toString() !== "") {
            setPaymentQuantityError({ isError: true, message: MESSAGES.QUANTITY_OVER + " kg." });
            return true;
        } else if (!localFormData.paymentQuantity) {
            setPaymentQuantityError({ isError: true, message: MESSAGES.SELECT_QUANTITY_FOR_PAYMENT });
        }
        setPaymentQuantityError({ isError: false, message: "" });
        return false;
    };


    const handleFormValidation = () => {
        let hasError = false;
        hasError = validateShipNumber(formData) || hasError;
        hasError = validateOfferQuotaType(formData) || hasError;
        hasError = validateOfferQuantity(formData, false) || hasError;
        hasError = validatePaymentQuotaType(formData) || hasError;
        hasError = validatePaymentQuantity(formData) || hasError;

        if (isFormDataComplete(formData)) {
            if (validateCodValueRatio(percentageOfCodValueOffered)) {
                hasError = true;
            }
        }

        return hasError;
    }

    /**
     * Checks if all the necessary formData fields are filled.
     *
     * @param {OfferPost} data - The form data object to validate.
     * @returns {boolean} - Returns `true` if all necessary fields are filled, otherwise `false`.
     */
    const isFormDataComplete = (data: OfferPost): boolean => {
        if (data.shipNumber && data.offerQuotaType && data.offerQuantity && data.paymentQuotaType && data.paymentQuantity) {
            return true;
        }
        else {
            return false;
        }
    };

    /**
     * Validates the provided cod value ratio against the minimum offer requirement.
     * Updates the codValueRatio error state based on the validation result.
     *
     * @param {number} codValueRatio - The cod value ratio to validate.
     * @returns {boolean} - Returns `true` if there is an error (i.e., if the provided ratio is less than the minimum offer), otherwise `false`.
     */
    const validateCodValueRatio = (codValueRatio: number | null): boolean => {
        if (codValueRatio == null) {
            return false;
        }
        if (codValueRatio < offerDetails.minimumOffer) {
            setPercentageOfCodValueOfferedError({ isError: true, message: MESSAGES.LOW_COD_VALUE_RATIO_ERROR });
            return true;
        } else {
            setPercentageOfCodValueOfferedError({ isError: false, message: "" });
            return false;
        }
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (isBusy) return;

        const hasError = handleFormValidation();
        if (hasError) {
            setSnackbarOpen(true);
            setSnackbarData({ message: MESSAGES.REGISTRATION_ERROR_CORRECTION, type: 'error' });
            return;
        }

        const offer = {
            offerMarketNumber: offerDetails.offerMarketNumber,
            shipNumber: formData.shipNumber,
            offerQuotaType: formData.offerQuotaType,
            offerQuantity: formData.offerQuantity,
            paymentQuotaType: formData.paymentQuotaType,
            paymentQuantity: formData.paymentQuantity
        };

        try {
            setIsSubmitting(true);
            await createOffer(offer);
            onClose(true);
            setIsSubmitting(false);
            // Reset the form.
            setFormData(offerFormInit);
            setRawOfferQuantity("");
            setRawPaymentQuantity("");
            setIsChecked(false);
        } catch (error) {
            handleUnauthorizedError(error);
            setIsSubmitting(false);
            setSnackbarOpen(true);
            setSnackbarData({ message: MESSAGES.OFFER_REGISTRATION_ERROR, type: 'error' });
        }

    };

    const fetchValueCoefficientAndPercentage = async () => {
        setIsFetchValueCoefficientAndPercentage(true);
        try {
            const offer: CalculateCodValueInput = {
                offerMarketNumber: offerDetails.offerMarketNumber,
                offerQuotaType: formData.offerQuotaType,
                offerQuantity: formData.offerQuantity,
                paymentQuotaType: formData.paymentQuotaType,
                paymentQuantity: formData.paymentQuantity
            };

            const codValueResult: CodValueResult = await getValueCoefficientAndPercentage(offer);
            setPercentageOfCodValueOffered(codValueResult.percentageOfCodValueOffered);
            setValueCoefficient(codValueResult.valueCoefficient);
            validateCodValueRatio(codValueResult.percentageOfCodValueOffered);
            setValueCoefficientError({ isError: false, message: "" })
        } catch (error) {
            handleUnauthorizedError(error);
            setSnackbarOpen(true);
            setSnackbarData({ message: MESSAGES.CALCULATION_ERROR, type: 'error' });
            setPercentageOfCodValueOffered(null);
            setValueCoefficient(null);
            setPercentageOfCodValueOfferedError({ isError: true, message: MESSAGES.COD_RATIO_CALCULATION_ERROR });
            setValueCoefficientError({ isError: true, message: MESSAGES.VALUE_COEFFICIENT_CALCULATION_ERROR });
        }
        setIsFetchValueCoefficientAndPercentage(false);
    };

    /**
     * `debouncedFetchValueCoefficientAndPercentage` is a debounced version of the `fetchValueCoefficientAndPercentage` function,
     * ensuring that it's invoked only after a specified delay. This technique limits the rate at which the function is called.
     * It is particularly useful for handling events that fire frequently and need to fetch data, like on user input or window resizing.
     * The `DEBOUNCE_TIME` constant defines the waiting period before the actual `fetchValueCoefficientAndPercentage` function is executed.
     */
    const debouncedFetchValueCoefficientAndPercentage = debounce(fetchValueCoefficientAndPercentage, DEBOUNCE_TIME);

    /**
     * Handles changes to the select fields.
     * @param event 
     */
    const handleSelectChange = (event: SelectChangeEvent) => {
        const { name, value } = event.target;
        setFormData(prevFormData => {
            const updatedFormData = {
                ...prevFormData,
                [name]: value
            };

            switch (name) {
                case 'shipNumber':
                    validateShipNumber(updatedFormData);
                    break;
                case 'offerQuotaType':
                    validateOfferQuotaType(updatedFormData);
                    validateOfferQuantity(updatedFormData, true);
                    break;
                case 'paymentQuotaType':
                    validatePaymentQuotaType(updatedFormData);
                    break;
            }

            return updatedFormData;
        });
    };

    /**
     * Handles changes to the input fields.
     * @param event
     */
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = event.target;

        const onlyDigitsValue = value.replace(/\D/g, ""); // Remove all non-digit characters

        if (name === 'offerQuantity') {
            setRawOfferQuantity(onlyDigitsValue);
        } else if (name === 'paymentQuantity') {
            setRawPaymentQuantity(onlyDigitsValue);
        }

        setFormData(prevFormData => {
            const updatedFormData = {
                ...prevFormData,
                [name]: onlyDigitsValue  // Store only the digits in the state for both fields
            };
            switch (name) {
                case 'offerQuantity':
                    validateOfferQuantity(updatedFormData, false);
                    break;
                case 'paymentQuantity':
                    validatePaymentQuantity(updatedFormData);
                    break;
            }
            return updatedFormData;
        });
    };

    const closeSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') return;
        setSnackbarOpen(false);
    };

    const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
        setIsChecked(event.target.checked);
    };

    const handleClose = () => {
        if (isBusy) return;
        onClose(false);
    };

    return (
        <div>
            {snackbarData && (
                <SnackbarMessage
                    message={snackbarData.message}
                    type={snackbarData.type}
                    open={snackbarOpen}
                    handleClose={closeSnackbar}
                />
            )}
            <Dialog
                open={open}
                fullWidth
                maxWidth="md"
                fullScreen={isMobile}
                onClose={handleClose}
                disableEscapeKeyDown={isBusy}
                PaperComponent={PaperComponent}
                aria-labelledby="offer-dialog-title"
            >
                <DialogTitle sx={{ cursor: isMobile ? 'default' : 'move', borderBottom: '1px solid', borderColor: 'divider' }} id="offer-dialog-title">
                    Skrá nýtt tilboð
                </DialogTitle>
                <DialogContent sx={{ pt: 3, '&.MuiDialogContent-root': { pt: 3 } }} aria-busy={isSubmitting || isLoading}>
                    {(isSubmitting || isLoading) && (
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            zIndex: 1000,
                            pointerEvents: 'none',
                        }}>
                            <CircularProgress />
                        </div>
                    )}
                    <Box sx={{ p: 2.5, mb: 3, bgcolor: 'primary.light', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                        Skilyrði þess að tilboð sé gilt, er að nægjanlegt aflamark í þeirri tegund sem láta á í skiptum sé á skipinu, og nægt magn af þeirri tegund sem óskað sé eftir sé í boði.
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>Gjald fyrir úthlutun aflaheimilda á tilboðsmarkaði: {offerDetails.allocationFee} krónur.</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {offerDetails.minimumOffer === 0 ? MESSAGES.NO_MINIMUM_OFFER : `${MESSAGES.MINIMUM_OFFER} ${offerDetails.minimumOffer}%`}
                    </Typography>
                    </Box>
                    <form id="offer-form" onSubmit={handleSubmit}>
                        <OfferFormSection number={1} title="Skip">
                            <FormControl fullWidth disabled={isLoading || isSubmitting || shipNumberError.hasNoShip}>
                                <InputLabel id="offer-ship-label" shrink>
                                    Skip
                                </InputLabel>
                                <Select
                                    id="offer-ship"
                                    labelId="offer-ship-label"
                                    aria-describedby="offer-ship-help"
                                    label="Skip"
                                    name="shipNumber"
                                    value={formData.shipNumber !== undefined ? String(formData.shipNumber) : ''}
                                    onChange={handleSelectChange}
                                    error={shipNumberError.isError}
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <FaShip />
                                        </InputAdornment>
                                    }
                                >
                                    {offerDetails.ships.map((option) => (
                                        <MenuItem
                                            key={option.number}
                                            value={option.number}
                                        >
                                            <Box component="span" sx={{ fontWeight: 700, mr: 1 }}>{option.number}</Box>{option.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText id="offer-ship-help" error={shipNumberError.isError}>
                                    {shipNumberError.isError === false ? MESSAGES.SELECT_SHIP_TYPE : shipNumberError.message}
                                </FormHelperText>
                            </FormControl>
                        </OfferFormSection>
                        <OfferFormSection number={2} title="Óska eftir">
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                            <FormControl fullWidth sx={{ minWidth: 0 }} variant="outlined" disabled={isLoading || isSubmitting || shipNumberError.hasNoShip}>
                                <InputLabel id="offer-quota-label" shrink>Fisktegund</InputLabel>
                                <Select
                                    id="offer-quota"
                                    labelId="offer-quota-label"
                                    aria-describedby="offer-quota-help"
                                    label="Fisktegund"
                                    name="offerQuotaType"
                                    value={formData.offerQuotaType !== undefined ? String(formData.offerQuotaType) : ''}
                                    onChange={handleSelectChange}
                                    error={offerQuotaTypeError.isError}
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <FaFish />
                                        </InputAdornment>
                                    }
                                >
                                    {offerDetails.offerQuota.map((option) => (
                                        <MenuItem key={option.name} value={option.quotaType}>
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText id="offer-quota-help" error={offerQuotaTypeError.isError}>
                                    {offerQuotaTypeError.isError === false
                                        ? MESSAGES.WISH_FOR_FISH_TYPE_FOR_PURCHASE
                                        : offerQuotaTypeError.message}
                                </FormHelperText>
                            </FormControl>
                            <FormControl fullWidth sx={{ minWidth: 0 }} variant="outlined" disabled={isLoading || isSubmitting || shipNumberError.hasNoShip}>
                                <InputLabel htmlFor="offer-quantity">Magn</InputLabel>
                                <OutlinedInput
                                    id="offer-quantity"
                                    aria-describedby="offer-quantity-help"
                                    label="Magn"
                                    value={formatNumber(rawOfferQuantity)}
                                    onChange={handleInputChange}
                                    error={offerQuantityError.isError}
                                    type="text"
                                    inputProps={{
                                        name: "offerQuantity",
                                        inputMode: "numeric",
                                    }}
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <AiOutlineNumber />
                                        </InputAdornment>
                                    }
                                    endAdornment={
                                        <InputAdornment position="end">
                                            {formData.offerQuotaType && tonQuotaTypes.some(tqt => tqt === formData.offerQuotaType) ? "tonn" : "kg"}
                                        </InputAdornment>
                                    }
                                    onKeyPress={(event) => {
                                        if (event.key === ".") {
                                            event.preventDefault();
                                        }
                                    }}
                                />
                                <FormHelperText id="offer-quantity-help" error={offerQuantityError.isError}>
                                    {offerQuantityError.isError
                                        ? offerQuantityError.message
                                        : (() => {
                                            const foundQuota = offerDetails.offerQuota.find(quota => quota.quotaType === formData.offerQuotaType);
                                            return foundQuota
                                                ? <>
                                                    {MESSAGES.QUANTITY_FOR_PURCHASE}
                                                    <span style={{ fontWeight: 600 }}>
                                                        {MESSAGES.MAX_QUOTA(
                                                            foundQuota.quantity,
                                                            formData.offerQuotaType && tonQuotaTypes.some(tqt => tqt === formData.offerQuotaType) ? "tonn" : "kg"
                                                        )}
                                                    </span>
                                                </>
                                                : MESSAGES.QUANTITY_FOR_PURCHASE;
                                        })()
                                    }
                                </FormHelperText>
                            </FormControl>
                        </Box>
                        </OfferFormSection>
                        <OfferFormSection number={3} title="Greiða með">
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                            <FormControl fullWidth sx={{ minWidth: 0 }} variant="outlined" disabled={isLoading || isSubmitting || shipNumberError.hasNoShip}>
                                <InputLabel id="payment-quota-label" shrink>Fisktegund</InputLabel>
                                <Select
                                    id="payment-quota"
                                    labelId="payment-quota-label"
                                    aria-describedby="payment-quota-help"
                                    label="Fisktegund"
                                    name="paymentQuotaType"
                                    value={formData.paymentQuotaType !== undefined ? String(formData.paymentQuotaType) : ''}
                                    onChange={handleSelectChange}
                                    error={paymentQuotaTypeError.isError}
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <FaFish />
                                        </InputAdornment>
                                    }
                                >
                                    {offerDetails.paymentQuota.map((option) => (
                                        <MenuItem key={option.name} value={option.quotaType}>
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText id="payment-quota-help" error={paymentQuotaTypeError.isError}>
                                    {paymentQuotaTypeError.isError === false
                                        ? <>
                                            {MESSAGES.FISH_TYPE_FOR_PAYMENT}<br />
                                            <strong style={{ fontWeight: 600 }}> Meðalverð aflamarks: {formattedAveragePrice} kr/kg.                                            </strong>
                                        </>
                                        : paymentQuotaTypeError.message
                                    }
                                </FormHelperText>
                            </FormControl>
                            <FormControl fullWidth sx={{ minWidth: 0 }} variant="outlined" disabled={isLoading || isSubmitting || shipNumberError.hasNoShip}>
                                <InputLabel htmlFor="payment-quantity">Magn</InputLabel>
                                <OutlinedInput
                                    id="payment-quantity"
                                    aria-describedby="payment-quantity-help"
                                    label="Magn"
                                    value={formatNumber(rawPaymentQuantity)}
                                    onChange={handleInputChange}
                                    error={paymentQuantityError.isError}
                                    type="text"
                                    inputProps={{
                                        name: "paymentQuantity",
                                        inputMode: "numeric",
                                    }}
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <AiOutlineNumber />
                                        </InputAdornment>
                                    }
                                    endAdornment={<InputAdornment position="end">kg</InputAdornment>}
                                    onKeyPress={(event) => {
                                        if (event.key === ".") {
                                            event.preventDefault();
                                        }
                                    }}
                                />
                                <FormHelperText id="payment-quantity-help" error={paymentQuantityError.isError}>
                                    {paymentQuantityError.isError === false
                                        ? MESSAGES.QUANTITY_TO_PAY
                                        : paymentQuantityError.message}
                                </FormHelperText>
                            </FormControl>
                        </Box>
                        </OfferFormSection>
                        <OfferFormSection number={4} title="Yfirfara tilboð">
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                            <FormControl fullWidth sx={{ minWidth: 0 }} variant="outlined" disabled={isLoading || isSubmitting || shipNumberError.hasNoShip}>
                                <OutlinedInput
                                    required
                                    id="offer-cod-ratio"
                                    inputProps={{ 'aria-label': 'Þorskígildihlutfall', 'aria-describedby': 'offer-cod-ratio-help' }}
                                    name="percentageOfCodValueOffered"
                                    readOnly
                                    value={percentageOfCodValueOffered !== null && percentageOfCodValueOffered !== undefined ? formatNumber(percentageOfCodValueOffered.toString()) : ""}
                                    error={percentageOfCodValueOfferedError.isError}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            {isFetchValueCoefficientAndPercentage ? <CircularProgress size={20} /> : '%'}
                                        </InputAdornment>
                                    }
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <MdPieChart />
                                        </InputAdornment>
                                    }
                                />
                                <FormHelperText id="offer-cod-ratio-help" error={percentageOfCodValueOfferedError.isError}>
                                    {percentageOfCodValueOfferedError.isError === false
                                        ? MESSAGES.COD_RATIO_TO_PAY
                                        : percentageOfCodValueOfferedError.message}
                                </FormHelperText>
                            </FormControl>
                            <FormControl fullWidth sx={{ minWidth: 0 }} variant="outlined" disabled={isLoading || isSubmitting || shipNumberError.hasNoShip}>
                                <OutlinedInput
                                    required
                                    id="offer-value-coefficient"
                                    inputProps={{ 'aria-label': 'Virðisstuðull', 'aria-describedby': 'offer-value-coefficient-help' }}
                                    name="valueCoefficient"
                                    readOnly
                                    value={valueCoefficient !== null && valueCoefficient !== undefined ? formatNumberFloat(valueCoefficient.toString()) : ""}
                                    error={valueCoefficientError && valueCoefficientError.isError}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            {isFetchValueCoefficientAndPercentage ? <CircularProgress size={20} /> : ''}
                                        </InputAdornment>
                                    }
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <MdPieChart />
                                        </InputAdornment>
                                    }
                                />
                                <FormHelperText id="offer-value-coefficient-help" error={valueCoefficientError.isError}>
                                    {valueCoefficientError && valueCoefficientError.isError === false
                                        ? MESSAGES.VALUE_COEFFICIENT_MESSAGE
                                        : valueCoefficientError.message}
                                </FormHelperText>
                            </FormControl>
                        </Box>
                        <Box sx={{ mt: 2.5, p: 2, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="acknowledgment"
                                        disabled={isLoading || isSubmitting || shipNumberError.hasNoShip}
                                        color="primary"
                                        onChange={handleCheckboxChange}
                                        checked={isChecked}
                                    />
                                }
                                label="Tilboð þetta er rétt og ég geri mér grein fyrir að það er bindandi."
                            />
                        </Box>
                        </OfferFormSection>
                    </form>
                </DialogContent>
                <DialogActions sx={{ borderTop: '1px solid', borderColor: 'divider', '& > button': { flexGrow: { xs: 1, sm: 0 } } }}>
                    <Button variant="outlined" disabled={isBusy} onClick={handleClose}>Loka</Button>
                    <Button variant="contained" type="submit" form="offer-form" disabled={isLoading || isSubmitting || shipNumberError.hasNoShip || !isChecked}>Skrá tilboð</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
